// React hook that fetches a boat's signal catalog from
// `GET /v1/boats/:boatId/signals` — the accepted-telemetry set the cloud
// compiler binds against (`boat_paths WHERE accepted`), merged with each path's
// latest sample and owning device. The custom-alarm builder's signal picker
// (phase 5) consumes this; the honesty invariant is that the picker only ever
// offers signals the compiler can actually bind, which is exactly this set.
//
// Per-boat and never module-cached (unlike the static `useAlertTemplates`
// library): the catalog grows as a boat reports more paths, and caching it
// module-wide would leak one boat's signals into another. Refetches on `boatId`
// change and on the manual `refresh()`; the pure grouping/labelling lives in
// `signalCatalog.ts`.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import type { Signal } from './signalCatalog';
import { useBoatNamesRevision } from './useBoatNames';

interface SignalsResponse {
  signals: Signal[];
}

export interface UseSignalCatalog {
  status: 'loading' | 'ready' | 'error';
  signals: Signal[];
  /** Present only when `status === 'error'`. */
  error?: string;
  /**
   * True while a manual {@link refresh} is in flight. A same-boat refresh keeps
   * the previous `status`, so this is the only signal a "Try again" button has
   * that anything happened — without it the click looks like it did nothing.
   */
  refreshing: boolean;
  refresh: () => void;
}

/**
 * What to put on screen for a failed fetch. 401/403 are the two cases we can
 * explain precisely; everything else gets one plain-English line rather than
 * `api()`'s internal string ("api /v1/boats/<uuid>/signals 500"), which names a
 * URL the reader did not type and a number they cannot act on.
 *
 * Exported for the unit test — the hook itself needs a DOM this package's
 * runner deliberately does not have, so the decision lives in a pure function.
 */
export function catalogErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 403) return 'You do not have access to this boat.';
  if (status === 401) return 'Please sign in again.';
  return 'We couldn’t reach your boat’s signal list. Check your connection and try again.';
}

export function useSignalCatalog(boatId: string): UseSignalCatalog {
  const namesRevision = useBoatNamesRevision(boatId);
  const [status, setStatus] = useState<UseSignalCatalog['status']>('loading');
  const [signals, setSignals] = useState<Signal[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  const lastRequestedBoatId = useRef<string | null>(null);
  const refresh = useCallback(() => {
    setRefreshing(true);
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (boatId === '') {
      setRefreshing(false);
      return;
    }
    let cancelled = false;
    const boatChanged = lastRequestedBoatId.current !== boatId;
    lastRequestedBoatId.current = boatId;
    // Reset to a clean loading state on a boat switch; keep the current signals
    // on a same-boat manual refresh so the picker doesn't flash empty.
    if (boatChanged) {
      setStatus('loading');
      setSignals([]);
      setError(undefined);
    }
    void (async () => {
      try {
        const data = await api<SignalsResponse>(`/v1/boats/${boatId}/signals`);
        if (cancelled) return;
        setSignals(Array.isArray(data?.signals) ? data.signals : []);
        setError(undefined);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(catalogErrorMessage(err));
        setStatus('error');
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick, namesRevision]);

  return { status, signals, error, refreshing, refresh };
}
